import { FC, ReactElement, memo, useCallback } from 'react';
import { useNavigate } from 'react-router';

import { useAppPagination } from '@/common/hooks';
import { AppPaginationOptions, AppRoutes } from '@/common/interfaces';
import { FallbackedLayout } from '@/common/layouts';

import { useChangeMainDomainProcessStatus } from '@/modules/Domains';
import { useUpdateSCAccountProcessStatus } from '@/modules/Domains/hooks';
import { useGetAllSiteDomainsQuery } from '@/modules/Domains/queries';
import { useGetContentByIdSitesQuery } from '@/modules/Sites';

import { SiteDomainsListView } from './SiteDomainsListView';

interface Props {
  siteId: string;
}

export const SiteDomainsListContainer: FC<Props> = memo(({ siteId }) => {
  const navigate = useNavigate();
  const { pageParam: currentPage, handleChangePage } = useAppPagination();

  const paginationOptions: AppPaginationOptions = { page: currentPage };

  const {
    data: siteData,
    error: siteError,
    isError: isSiteError,
    isLoading: isSiteLoading,
  } = useGetContentByIdSitesQuery(siteId)({
    enabled: !!siteId
  });

  const {
    data: siteDomainsData,
    error: siteDomainsError,
    isError: isSiteDomainsError,
    isLoading: isSiteDomainsLoading,
    isFetching: isSiteDomainsFetching,
  } = useGetAllSiteDomainsQuery(siteId, paginationOptions)({
    enabled: !!siteId
  });

  useUpdateSCAccountProcessStatus({
    siteId,
    pageParam: paginationOptions,
  });

  useChangeMainDomainProcessStatus({ siteId, pageParam: paginationOptions, });

  const handleBack = useCallback((): void => {
    navigate(AppRoutes.SitesById.replace(':siteId', siteId));
  }, [siteId]);

  return (
    <FallbackedLayout
      data={ siteDomainsData }
      error={ siteDomainsError || siteError }
      isError={ isSiteDomainsError || isSiteError }
      isLoading={ isSiteDomainsLoading || isSiteLoading }
    >
      { (domainsList): ReactElement => (
        <SiteDomainsListView
          siteId={ siteId }
          isAutoRegluingsEnabled={ siteData?.isAutoRegluingsEnabled ?? false }
          currentPage={ currentPage }
          siteDomainsItems={ domainsList.items }
          siteDomainsTotalItems={ domainsList.meta.totalItems }
          siteDomainsItemsPerPage={ domainsList.meta.itemsPerPage }
          isLoading={ isSiteDomainsFetching }
          handleBack={ handleBack }
          handleChangePage={ handleChangePage }
        />
      ) }
    </FallbackedLayout>
  );
});
